import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Button, IconButton, Badge, Paper, Fade, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useCopilot } from '../contexts/CopilotContext';
import MillosLogo from './MillosLogo';

const POSITION_STORAGE_KEY = 'copilot_bubble_position';
const DRAG_THRESHOLD_PX = 6;
const EDGE_MARGIN_PX = 8;
const BUTTON_SIZE_PX = 56;

const clampPosition = (bottom: number, right: number) => ({
  bottom: Math.min(Math.max(bottom, EDGE_MARGIN_PX), window.innerHeight - BUTTON_SIZE_PX - EDGE_MARGIN_PX),
  right: Math.min(Math.max(right, EDGE_MARGIN_PX), window.innerWidth - BUTTON_SIZE_PX - EDGE_MARGIN_PX),
});

// Re-export so callers that import CopilotMessage from here still work
export type { CopilotMessage } from '../contexts/CopilotContext';

const CopilotBubble: React.FC = () => {
  const { palette } = useTheme();
  const { message, open, badgeActive, openBubble, closeBubble, advanceQueue, acknowledgeMessage, dismissForNow, clearMessage } = useCopilot();

  // Draggable position (touch or mouse) — persisted so the agent stays wherever it was last left,
  // since on a phone it can end up sitting over content that needs a tap.
  const [position, setPosition] = useState(() => {
    try {
      const saved = localStorage.getItem(POSITION_STORAGE_KEY);
      return saved ? clampPosition(JSON.parse(saved).bottom, JSON.parse(saved).right) : { bottom: 28, right: 28 };
    } catch {
      return { bottom: 28, right: 28 };
    }
  });
  const dragState = useRef<{ startX: number; startY: number; startBottom: number; startRight: number; dragging: boolean } | null>(null);
  const wasDraggedRef = useRef(false);

  // Keep it on-screen after an orientation change / window resize.
  useEffect(() => {
    const onResize = () => setPosition(p => clampPosition(p.bottom, p.right));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleDragPointerDown = (e: React.PointerEvent) => {
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragState.current = { startX: e.clientX, startY: e.clientY, startBottom: position.bottom, startRight: position.right, dragging: false };
  };

  const handleDragPointerMove = (e: React.PointerEvent) => {
    const drag = dragState.current;
    if (!drag) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (!drag.dragging && Math.hypot(dx, dy) > DRAG_THRESHOLD_PX) {
      drag.dragging = true;
    }
    if (drag.dragging) {
      setPosition(clampPosition(drag.startBottom - dy, drag.startRight - dx));
    }
  };

  const handleDragPointerUp = () => {
    if (dragState.current?.dragging) {
      wasDraggedRef.current = true;
      setPosition(p => {
        try { localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(p)); } catch { /* ignore */ }
        return p;
      });
    }
    dragState.current = null;
  };

  const handleIconClick = () => {
    // A drag ending on the button still fires a click right after pointerup — swallow that one
    // so dragging the agent out of the way doesn't also toggle it open.
    if (wasDraggedRef.current) {
      wasDraggedRef.current = false;
      return;
    }
    open ? closeBubble() : openBubble();
  };

  const handlePrimaryAction = () => {
    if (message?.confirm) {
      message.confirm.onConfirm();
      advanceQueue(); // confirm = one-shot, clear after
    } else if (message?.action) {
      message.action.onClick();
      closeBubble(); // keep message so user can reopen it by clicking the icon
    }
  };

  const handleSecondaryAction = () => {
    if (message?.secondaryAction) {
      message.secondaryAction.onClick();
    }
    closeBubble();
  };

  const handleCancel = () => {
    clearMessage();
  };

  return (
    // pointerEvents: 'none' on the outer fixed container, with 'auto' re-enabled on the actual
    // visible pieces below — otherwise the closed chat panel's DOM node (kept mounted by Fade,
    // just invisible) can silently sit over whatever page content happens to render underneath
    // this fixed position and swallow its clicks, depending only on where the bubble was last
    // dragged to. A real bug this caused: certificate/reminder action icons near the bubble's
    // position became unclickable with no visible cause.
    <Box sx={{ position: 'fixed', bottom: position.bottom, right: position.right, zIndex: 1400, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1.5, pointerEvents: 'none' }}>

      {/* Chat bubble */}
      <Fade in={open && !!message}>
        <Paper
          elevation={6}
          sx={{
            maxWidth: 300, borderRadius: '16px', overflow: 'visible',
            border: '1px solid #818cf8',
            boxShadow: '0 8px 32px rgba(99,102,241,0.18)',
            pointerEvents: 'auto',
          }}
        >
          {/* Header */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            px: 2, py: 1.25,
            bgcolor: '#1e1b4b', borderRadius: '16px 16px 0 0',
          }}>
            <MillosLogo size={22} agent />
            <Typography sx={{ flex: 1, fontWeight: 700, fontSize: '0.82rem', color: '#c7d2fe', letterSpacing: 0.3 }}>
              Imperial Copilot
            </Typography>
            {/* X = hard dismiss: clears queue, hides badge, keeps message for icon recall */}
            <IconButton size="small" onClick={dismissForNow} sx={{ color: 'rgba(199,210,254,0.6)', p: 0.25, '&:hover': { color: '#c7d2fe' } }}>
              <CloseIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Box>

          {/* Message body */}
          <Box sx={{ px: 2, py: 1.75, bgcolor: palette.tokens.white, borderRadius: '0 0 16px 16px' }}>
            <Typography sx={{ fontSize: '0.85rem', color: palette.tokens.surfaceDarkElevated, lineHeight: 1.55 }}>
              {message?.text}
            </Typography>

            {/* Confirm pattern */}
            {message?.confirm && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                <Button size="small" variant="contained" onClick={handlePrimaryAction}
                  sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.78rem', borderRadius: '8px', boxShadow: 'none', bgcolor: palette.tokens.error, '&:hover': { bgcolor: '#b91c1c', boxShadow: 'none' } }}>
                  {message.confirm.label}
                </Button>
                <Button size="small" variant="text" onClick={handleCancel}
                  sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', borderRadius: '8px', color: palette.tokens.muted, '&:hover': { bgcolor: palette.tokens.surfaceSoft } }}>
                  Cancel
                </Button>
              </Box>
            )}

            {/* Action buttons (non-confirm) */}
            {!message?.confirm && (message?.action || message?.secondaryAction) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                {message?.action && (
                  <Button size="small" variant="contained" onClick={handlePrimaryAction}
                    sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.78rem', borderRadius: '8px', boxShadow: 'none', bgcolor: palette.tokens.ink, '&:hover': { bgcolor: palette.tokens.surfaceDarkElevated, boxShadow: 'none' } }}>
                    {message.action.label}
                  </Button>
                )}
                {message?.secondaryAction && (
                  <Button size="small" variant="text" onClick={handleSecondaryAction}
                    sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', borderRadius: '8px', color: palette.tokens.muted, '&:hover': { bgcolor: palette.tokens.surfaceSoft, color: palette.tokens.ink } }}>
                    {message.secondaryAction.label}
                  </Button>
                )}
              </Box>
            )}

            {/* "Got it" / "I'll do it later" — advances queue or closes+hides badge */}
            {message?.laterLabel && (
              <Box sx={{ mt: 1.25 }}>
                <Typography
                  onClick={acknowledgeMessage}
                  sx={{ fontSize: '0.72rem', color: palette.tokens.mutedSoft, cursor: 'pointer', display: 'inline', '&:hover': { color: palette.tokens.muted, textDecoration: 'underline' } }}
                >
                  {message.laterLabel}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Speech bubble tail */}
          <Box sx={{ position: 'absolute', bottom: -8, right: 24, width: 0, height: 0, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderTop: `8px solid ${palette.tokens.white}` }} />
        </Paper>
      </Fade>

      {/* Floating button */}
      <Badge
        badgeContent={badgeActive ? '!' : 0}
        color="error"
        overlap="circular"
        sx={{ pointerEvents: 'auto', '& .MuiBadge-badge': { fontSize: '0.65rem', fontWeight: 900, minWidth: 18, height: 18 } }}
      >
        <Box
          onClick={handleIconClick}
          onPointerDown={handleDragPointerDown}
          onPointerMove={handleDragPointerMove}
          onPointerUp={handleDragPointerUp}
          sx={{
            width: 56, height: 56, borderRadius: '50%',
            bgcolor: '#1e1b4b',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'grab',
            touchAction: 'none', // don't let the page try to scroll/refresh while dragging the agent
            userSelect: 'none',
            border: badgeActive ? '2px solid #818cf8' : '2px solid #4338ca',
            boxShadow: badgeActive ? '0 4px 20px rgba(99,102,241,0.45)' : '0 2px 10px rgba(30,27,75,0.4)',
            transition: 'box-shadow 0.2s, transform 0.2s',
            '&:hover': { transform: 'scale(1.07)', boxShadow: '0 6px 24px rgba(99,102,241,0.5)' },
            '&:active': { cursor: 'grabbing' },
            ...(badgeActive && !open && {
              animation: 'agent-pulse 2s ease-in-out infinite',
              '@keyframes agent-pulse': {
                '0%':  { boxShadow: '0 0 0 0 rgba(99,102,241,0.6)' },
                '60%': { boxShadow: '0 0 0 10px rgba(99,102,241,0)' },
                '100%':{ boxShadow: '0 0 0 0 rgba(99,102,241,0)' },
              },
            }),
          }}
        >
          <MillosLogo size={34} agent />
        </Box>
      </Badge>
    </Box>
  );
};

export default CopilotBubble;
